/**
 * Live Chat updates pushed by the server (GET /api/live-chat/events, Server-Sent Events): every new chat
 * message and every sent / delivered / read change arrives here as it happens, so the chat never has to be
 * refreshed or polled.
 *
 * One connection is shared by everything that subscribes; it opens with the first subscriber and closes with
 * the last. It reconnects by itself (1s, 2s, 4s … up to 30s, and at once when the internet comes back) and
 * then calls onResync so the open chat can pick up anything sent while it was offline.
 *
 * fetch() instead of EventSource: EventSource can't send the login token in the Authorization header.
 */
import { getAuthUser } from "../../util/manageAuth";

/** One chat row, same fields as /conversations/search. */
export interface ChatEvent {
  created: boolean; // false = an existing message changed (e.g. its status)
  id: number;
  mobile: string;
  message?: string | null;
  messageType?: string | null;
  direction?: "INBOUND" | "OUTBOUND" | string | null;
  waMessageId?: string | null;
  status?: string | null;
  timestamp?: number | null;
  mediaId?: string | null;
  mediaUrl?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  caption?: string | null;
}

export type LiveState = "connecting" | "live" | "offline";

export interface ChatEventListener {
  onMessage?: (e: ChatEvent) => void;
  onState?: (s: LiveState) => void;
  /** Connected again after a drop: reload what's on screen once. */
  onResync?: () => void;
}

const MAX_DELAY = 30_000;

const listeners = new Set<ChatEventListener>();
let state: LiveState = "connecting";
let controller: AbortController | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
let connectedBefore = false;

const setState = (s: LiveState) => {
  if (s === state) return;
  state = s;
  listeners.forEach((l) => l.onState?.(s));
};

/** Same number whatever the format: "+91 98765 43210", "919876543210" and "9876543210" match. */
export const phoneKey = (phone?: string | null) => {
  const d = String(phone ?? "").replace(/\D/g, "");
  return d.length > 10 ? d.slice(-10) : d;
};

export const samePhone = (a?: string | null, b?: string | null) => {
  const ka = phoneKey(a);
  return !!ka && ka === phoneKey(b);
};

function handle(raw: string) {
  let event = "message";
  const data: string[] = [];
  raw.split("\n").forEach((line) => {
    if (!line || line.startsWith(":")) return; // comment = heartbeat
    const i = line.indexOf(":");
    const field = i < 0 ? line : line.slice(0, i);
    const value = i < 0 ? "" : line.slice(i + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  });

  if (event === "ready") {
    attempt = 0;
    setState("live");
    if (connectedBefore) listeners.forEach((l) => l.onResync?.());
    connectedBefore = true;
    return;
  }
  if (event === "message" && data.length) {
    try {
      const row = JSON.parse(data.join("\n")) as ChatEvent;
      listeners.forEach((l) => l.onMessage?.(row));
    } catch {
      /* not JSON: ignore */
    }
  }
}

async function connect() {
  const token = getAuthUser()?.accessToken;
  if (!token) {
    setState("offline");
    return;
  }
  const mine = new AbortController();
  controller = mine;
  setState("connecting");

  try {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL || ""}/api/live-chat/events`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
      cache: "no-store",
      signal: mine.signal,
    });
    // Signed out / not allowed: trying again won't help
    if (res.status === 401 || res.status === 403) {
      setState("offline");
      return;
    }
    if (!res.ok || !res.body) throw new Error(`Live chat events: HTTP ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true }).replace(/\r\n?/g, "\n");
      let end;
      while ((end = buf.indexOf("\n\n")) >= 0) {
        handle(buf.slice(0, end));
        buf = buf.slice(end + 2);
      }
    }
    throw new Error("Live chat events: connection closed");
  } catch {
    if (mine.signal.aborted || controller !== mine) return; // closed on purpose
    retry();
  }
}

function retry() {
  setState(connectedBefore ? "offline" : "connecting");
  if (retryTimer || !listeners.size) return;
  const delay = Math.min(MAX_DELAY, 1000 * 2 ** attempt) + Math.random() * 500;
  attempt++;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (listeners.size) connect();
  }, delay);
}

const reconnectNow = () => {
  if (!listeners.size || state === "live") return;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  attempt = 0;
  controller?.abort();
  connect();
};

function disconnect() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  controller?.abort();
  controller = null;
  attempt = 0;
  connectedBefore = false;
  state = "connecting";
  window.removeEventListener("online", reconnectNow);
}

/** Starts receiving live chat events; call the returned function to stop. */
export function subscribeChatEvents(listener: ChatEventListener): () => void {
  listeners.add(listener);
  listener.onState?.(state);
  if (listeners.size === 1) {
    window.addEventListener("online", reconnectNow);
    connect();
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) disconnect();
  };
}
